import React, {
    useState, useContext, useCallback
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { TranslatorContext } from '@jutro/locale';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { TermsAndConditionUtils } from 'gw-portals-util-js';
import {
    Button,
    ModalNext,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Tooltip,
    IconButton,
    Loader
} from '@jutro/components';
import metadata from './PaperlessComponent.metadata.json5';
import messages from './PaperlessComponent.messages';
import styles from './PaperlessComponent.module.scss';

function PaperlessComponent({
    // eslint-disable-next-line no-unused-vars
    authHeader, policyNumber, offeringName, pniEmail, user_name: userName, email,
    tncTransactionTypesSubTypes, terms, isOpen, onResolve
}) {
    const translator = useContext(TranslatorContext);
    const { EndorsementService } = useDependencies('EndorsementService');
    const [formData, updateFormData] = useState({
        policyNumber: policyNumber,
        isPaperlessEnabled: true,
        tncAccepted: false
    });
    const [paperlessPreferenceDone, setPaperlessPreferenceDone] = useState(false);
    const [isLoading, setLoadingState] = useState(false);
    const [errorState, SetErrorState] = useState(false);

    const renderPaperlessPreferredTooltip = useCallback(
        () => {
            return (
                <Tooltip
                    placement="top"
                    trigger="mouseenter"
                    content={translator(messages.paperlessPreferredTooltip)}
                >
                    <IconButton icon="mi-info" className={styles.paperlessPreferredTooltip} />
                </Tooltip>
            );
        },
        [translator]
    );

    const onCancelClick = useCallback(() => {
        return onResolve();
    }, [onResolve]);

    const onActionClick = useCallback(() => {
        if (!paperlessPreferenceDone) {
            setLoadingState(true);
            EndorsementService.performPaperlessChange(
                [formData.policyNumber, formData.isPaperlessEnabled],
                authHeader
            ).then((changeJobNumber) => {
                if (changeJobNumber) {
                    TermsAndConditionUtils.saveTnCAcceptance(_.get(terms, 'Version', ''), tncTransactionTypesSubTypes, 'PolicyChange', 'paperlessPreference', authHeader, changeJobNumber, userName, email);
                    SetErrorState(false);
                } else {
                    SetErrorState(true);
                }
                setPaperlessPreferenceDone(true);
                setLoadingState(false);
            }).catch(() => {
                setPaperlessPreferenceDone(false);
                SetErrorState(true);
                setLoadingState(false);
            });
        } else {
            onResolve(true);
        }
    }, [paperlessPreferenceDone, EndorsementService, formData, authHeader,
        terms, tncTransactionTypesSubTypes, userName, email, onResolve]);


    const dataForComponent = {
        ...formData
    };

    const handleChange = useCallback((value, changedPath) => {
        const data = { ...formData };
        _.set(data, changedPath, value);
        updateFormData(data);
    }, [formData]);

    const handleChangeTnc = useCallback((value) => {
        const data = { ...formData };
        _.set(data, 'tncAccepted', value);
        updateFormData(data);
    }, [formData]);

    const disableActionButton = useCallback(() => {
        if (!formData.tncAccepted || !formData.isPaperlessEnabled || !pniEmail || isLoading) {
            return true;
        }
        return false;
    }, [formData.isPaperlessEnabled, formData.tncAccepted, isLoading, pniEmail]);

    const overrideProps = {
        termsAndConditionsCheckbox: {
            value: formData.tncAccepted,
            onValueChange: handleChangeTnc
        },
        paperlessPreferredTooltip: {
            content: renderPaperlessPreferredTooltip()
        },
        profileChangeContainerDiv: {
            visible: !paperlessPreferenceDone
        },
        paperlessChangeConfirmationDiv: {
            visible: paperlessPreferenceDone && !errorState
        },
        paperlessErrorDiv: {
            visible: errorState
        },
        paperlessPreferenceReviewSection: {
            content: pniEmail ? translator(messages.paperlessReviewMsgWithEmail, { pniEmail })
                : translator(messages.paperlessReviewMsgWithOutEmail, { policyNumber })
        }
    };
    const resolvers = {
        resolveClassNameMap: styles
    };
    return (
        <ModalNext isOpen={isOpen}>
            <ModalHeader
                title={`${offeringName} (${policyNumber})`}
            />
            <ModalBody>
                {isLoading ? (
                    <Loader loaded={!isLoading} />
                ) : (
                    <ViewModelForm
                        uiProps={metadata.pageContent}
                        model={dataForComponent}
                        onValueChange={handleChange}
                        overrideProps={overrideProps}
                        classNameMap={resolvers.resolveClassNameMap}
                    />
                )}
            </ModalBody>
            <ModalFooter>
                {!paperlessPreferenceDone ? (
                    <Button onClick={onCancelClick} type="secondary" disabled={isLoading}>
                        {messages.cancel}
                    </Button>
                ) : null }
                <Button
                    onClick={onActionClick}
                    disabled={disableActionButton()}
                >
                    {!paperlessPreferenceDone ? messages.submit : messages.ok}
                </Button>
            </ModalFooter>
        </ModalNext>
    );
}

PaperlessComponent.propTypes = {
    authHeader: PropTypes.shape({
        Authorization: PropTypes.string
    }).isRequired,
    policyNumber: PropTypes.string.isRequired,
    offeringName: PropTypes.string.isRequired,
    pniEmail: PropTypes.string,
    user_name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    tncTransactionTypesSubTypes: PropTypes.shape({}).isRequired,
    terms: PropTypes.shape({}).isRequired,
    onResolve: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired
};

PaperlessComponent.defaultProps = {
    pniEmail: undefined
};

export default withAuthenticationContext(PaperlessComponent);
